import type * as Types from '../../../../graphql/generated-base';

import type { DocumentNode } from 'graphql';
import * as Apollo from '@apollo/client';
import * as ApolloReactHooks from '@graphql/react-hooks';
export type CashFundGeneralErrorFragment = { __typename?: 'CashFundGeneralError'; code: string; nativeMessage?: string | null };

export type CashFundPhotoFragment = { __typename?: 'Photo'; id: string; url: string };

export type PublicCashFundFragment = {
  __typename?: 'CashFund';
  id: string;
  name: string;
  note?: string | null;
  currencyCode: string;
  archived: boolean;
  publicTotalContributedAmount?: {
    __typename?: 'MonetaryValue';
    valueInMinorUnits: number;
    floatingPointDecimalString: string;
    currency?: { __typename?: 'Currency'; code: string; places: number } | null;
  } | null;
  publicTotalGoal?: {
    __typename?: 'MonetaryValue';
    valueInMinorUnits: number;
    floatingPointDecimalString: string;
    currency?: { __typename?: 'Currency'; code: string; places: number } | null;
  } | null;
  fixedContributionAmount?: {
    __typename?: 'MonetaryValue';
    valueInMinorUnits: number;
    floatingPointDecimalString: string;
    currency?: { __typename?: 'Currency'; code: string; places: number } | null;
  } | null;
  photo?: { __typename?: 'Photo'; id: string; url: string } | null;
};

export type AdminCashFundFragment = {
  __typename?: 'CashFund';
  hideGoal: boolean;
  id: string;
  name: string;
  note?: string | null;
  currencyCode: string;
  archived: boolean;
  totalGoal?: {
    __typename?: 'MonetaryValue';
    valueInMinorUnits: number;
    floatingPointDecimalString: string;
    currency?: { __typename?: 'Currency'; code: string; places: number } | null;
  } | null;
  totalContributedAmount?: {
    __typename?: 'MonetaryValue';
    valueInMinorUnits: number;
    floatingPointDecimalString: string;
    currency?: { __typename?: 'Currency'; code: string; places: number } | null;
  } | null;
  contributions: Array<{
    __typename?: 'CashFundContribution';
    id: string;
    message?: string | null;
    status: string;
    createdAt: string;
    amount: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    };
    contributor: { __typename?: 'CashFundContributor'; id: string; fullName: string; email: string };
  }>;
  publicTotalContributedAmount?: {
    __typename?: 'MonetaryValue';
    valueInMinorUnits: number;
    floatingPointDecimalString: string;
    currency?: { __typename?: 'Currency'; code: string; places: number } | null;
  } | null;
  publicTotalGoal?: {
    __typename?: 'MonetaryValue';
    valueInMinorUnits: number;
    floatingPointDecimalString: string;
    currency?: { __typename?: 'Currency'; code: string; places: number } | null;
  } | null;
  fixedContributionAmount?: {
    __typename?: 'MonetaryValue';
    valueInMinorUnits: number;
    floatingPointDecimalString: string;
    currency?: { __typename?: 'Currency'; code: string; places: number } | null;
  } | null;
  photo?: { __typename?: 'Photo'; id: string; url: string } | null;
};

export type CashFundContributorFragment = { __typename?: 'CashFundContributor'; id: string; fullName: string; email: string };

export type CashFundContributionFragment = {
  __typename?: 'CashFundContribution';
  id: string;
  message?: string | null;
  status: string;
  createdAt: string;
  amount: {
    __typename?: 'MonetaryValue';
    valueInMinorUnits: number;
    floatingPointDecimalString: string;
    currency?: { __typename?: 'Currency'; code: string; places: number } | null;
  };
  contributor: { __typename?: 'CashFundContributor'; id: string; fullName: string; email: string };
};

export type CashFundOrderFragment = {
  __typename?: 'CashFundOrder';
  id: string;
  status: string;
  contributionAmount: {
    __typename?: 'MonetaryValue';
    valueInMinorUnits: number;
    floatingPointDecimalString: string;
    currency?: { __typename?: 'Currency'; code: string; places: number } | null;
  };
  totalTaxes: {
    __typename?: 'MonetaryValue';
    valueInMinorUnits: number;
    floatingPointDecimalString: string;
    currency?: { __typename?: 'Currency'; code: string; places: number } | null;
  };
  totalFees: {
    __typename?: 'MonetaryValue';
    valueInMinorUnits: number;
    floatingPointDecimalString: string;
    currency?: { __typename?: 'Currency'; code: string; places: number } | null;
  };
  total: {
    __typename?: 'MonetaryValue';
    valueInMinorUnits: number;
    floatingPointDecimalString: string;
    currency?: { __typename?: 'Currency'; code: string; places: number } | null;
  };
};

export type CashFundsPayoutAccountFragment = {
  __typename?: 'CashFundsPayoutAccount';
  status: string;
  currencyCode: string;
  countryCode?: string | null;
  onboardingUrl?: string | null;
  balance: {
    __typename?: 'CashFundsPayoutAccountBalance';
    received: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    };
    availablePending: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    };
    available: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    };
    withdrawnPending: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    };
    withdrawn: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    };
  };
  withdrawals: Array<{ __typename?: 'CashFundsPayoutAccountWithdrawal'; id: string; status: string }>;
};

export type CashFundInviteFragment = { __typename?: 'CashFundSolicitationInvite'; id: string; email: string; fullName?: string | null; guestKey?: string | null };

export type PublicCashFundsHostFragment = { __typename?: 'CashFundsHost'; id: string; firstName?: string | null; lastName?: string | null };

export type AdminCashFundsHostFragment = {
  __typename?: 'CashFundsHost';
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  payoutAccount: {
    __typename?: 'CashFundsPayoutAccount';
    status: string;
    currencyCode: string;
    countryCode?: string | null;
    onboardingUrl?: string | null;
    balance: {
      __typename?: 'CashFundsPayoutAccountBalance';
      received: {
        __typename?: 'MonetaryValue';
        valueInMinorUnits: number;
        floatingPointDecimalString: string;
        currency?: { __typename?: 'Currency'; code: string; places: number } | null;
      };
      availablePending: {
        __typename?: 'MonetaryValue';
        valueInMinorUnits: number;
        floatingPointDecimalString: string;
        currency?: { __typename?: 'Currency'; code: string; places: number } | null;
      };
      available: {
        __typename?: 'MonetaryValue';
        valueInMinorUnits: number;
        floatingPointDecimalString: string;
        currency?: { __typename?: 'Currency'; code: string; places: number } | null;
      };
      withdrawnPending: {
        __typename?: 'MonetaryValue';
        valueInMinorUnits: number;
        floatingPointDecimalString: string;
        currency?: { __typename?: 'Currency'; code: string; places: number } | null;
      };
      withdrawn: {
        __typename?: 'MonetaryValue';
        valueInMinorUnits: number;
        floatingPointDecimalString: string;
        currency?: { __typename?: 'Currency'; code: string; places: number } | null;
      };
    };
    withdrawals: Array<{ __typename?: 'CashFundsPayoutAccountWithdrawal'; id: string; status: string }>;
  };
  cashFunds: Array<{
    __typename?: 'CashFund';
    hideGoal: boolean;
    id: string;
    name: string;
    note?: string | null;
    currencyCode: string;
    archived: boolean;
    totalGoal?: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    } | null;
    totalContributedAmount?: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    } | null;
    contributions: Array<{
      __typename?: 'CashFundContribution';
      id: string;
      message?: string | null;
      status: string;
      createdAt: string;
      amount: {
        __typename?: 'MonetaryValue';
        valueInMinorUnits: number;
        floatingPointDecimalString: string;
        currency?: { __typename?: 'Currency'; code: string; places: number } | null;
      };
      contributor: { __typename?: 'CashFundContributor'; id: string; fullName: string; email: string };
    }>;
    publicTotalContributedAmount?: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    } | null;
    publicTotalGoal?: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    } | null;
    fixedContributionAmount?: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    } | null;
    photo?: { __typename?: 'Photo'; id: string; url: string } | null;
  }>;
};

export type CashFundSolicitationFragment = {
  __typename?: 'CashFundSolicitation';
  id: string;
  name?: string | null;
  status: string;
  inviteSubject: string;
  inviteMessage: string;
  config: {
    __typename?: 'CashFundSolicitationConfig';
    buttonText: string;
    salutation: string;
    includeRecipientNames: boolean;
    photo?: { __typename?: 'Photo'; id: string; url: string } | null;
  };
  invites: Array<{ __typename?: 'CashFundSolicitationInvite'; id: string; email: string; fullName?: string | null; guestKey?: string | null }>;
};

export type FindCashFundsHostByEventIdQueryVariables = Types.Exact<{
  eventId: Types.Scalars['ID'];
  returnRoute?: Types.InputMaybe<Types.Scalars['String']>;
}>;

export type FindCashFundsHostByEventIdQuery = {
  __typename?: 'Query';
  findCashFundsHostByEventId?: {
    __typename?: 'CashFundsHost';
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    payoutAccount: {
      __typename?: 'CashFundsPayoutAccount';
      status: string;
      currencyCode: string;
      countryCode?: string | null;
      onboardingUrl?: string | null;
      balance: {
        __typename?: 'CashFundsPayoutAccountBalance';
        received: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        };
        availablePending: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        };
        available: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        };
        withdrawnPending: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        };
        withdrawn: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        };
      };
      withdrawals: Array<{ __typename?: 'CashFundsPayoutAccountWithdrawal'; id: string; status: string }>;
    };
    cashFunds: Array<{
      __typename?: 'CashFund';
      hideGoal: boolean;
      id: string;
      name: string;
      note?: string | null;
      currencyCode: string;
      archived: boolean;
      totalGoal?: {
        __typename?: 'MonetaryValue';
        valueInMinorUnits: number;
        floatingPointDecimalString: string;
        currency?: { __typename?: 'Currency'; code: string; places: number } | null;
      } | null;
      totalContributedAmount?: {
        __typename?: 'MonetaryValue';
        valueInMinorUnits: number;
        floatingPointDecimalString: string;
        currency?: { __typename?: 'Currency'; code: string; places: number } | null;
      } | null;
      contributions: Array<{
        __typename?: 'CashFundContribution';
        id: string;
        message?: string | null;
        status: string;
        createdAt: string;
        amount: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        };
        contributor: { __typename?: 'CashFundContributor'; id: string; fullName: string; email: string };
      }>;
      publicTotalContributedAmount?: {
        __typename?: 'MonetaryValue';
        valueInMinorUnits: number;
        floatingPointDecimalString: string;
        currency?: { __typename?: 'Currency'; code: string; places: number } | null;
      } | null;
      publicTotalGoal?: {
        __typename?: 'MonetaryValue';
        valueInMinorUnits: number;
        floatingPointDecimalString: string;
        currency?: { __typename?: 'Currency'; code: string; places: number } | null;
      } | null;
      fixedContributionAmount?: {
        __typename?: 'MonetaryValue';
        valueInMinorUnits: number;
        floatingPointDecimalString: string;
        currency?: { __typename?: 'Currency'; code: string; places: number } | null;
      } | null;
      photo?: { __typename?: 'Photo'; id: string; url: string } | null;
    }>;
  } | null;
};

export type GetCashFundsByEventIdQueryVariables = Types.Exact<{
  eventId: Types.Scalars['ID'];
}>;

export type GetCashFundsByEventIdQuery = {
  __typename?: 'Query';
  getCashFundsByEventId: Array<{
    __typename?: 'CashFund';
    hideGoal: boolean;
    id: string;
    name: string;
    note?: string | null;
    currencyCode: string;
    archived: boolean;
    totalGoal?: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    } | null;
    totalContributedAmount?: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    } | null;
    contributions: Array<{
      __typename?: 'CashFundContribution';
      id: string;
      message?: string | null;
      status: string;
      createdAt: string;
      amount: {
        __typename?: 'MonetaryValue';
        valueInMinorUnits: number;
        floatingPointDecimalString: string;
        currency?: { __typename?: 'Currency'; code: string; places: number } | null;
      };
      contributor: { __typename?: 'CashFundContributor'; id: string; fullName: string; email: string };
    }>;
    publicTotalContributedAmount?: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    } | null;
    publicTotalGoal?: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    } | null;
    fixedContributionAmount?: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    } | null;
    photo?: { __typename?: 'Photo'; id: string; url: string } | null;
  }>;
};

export type GetCashFundByIdQueryVariables = Types.Exact<{
  id: Types.Scalars['ID'];
}>;

export type GetCashFundByIdQuery = {
  __typename?: 'Query';
  getCashFundById: {
    __typename?: 'CashFund';
    hideGoal: boolean;
    id: string;
    name: string;
    note?: string | null;
    currencyCode: string;
    archived: boolean;
    totalGoal?: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    } | null;
    totalContributedAmount?: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    } | null;
    contributions: Array<{
      __typename?: 'CashFundContribution';
      id: string;
      message?: string | null;
      status: string;
      createdAt: string;
      amount: {
        __typename?: 'MonetaryValue';
        valueInMinorUnits: number;
        floatingPointDecimalString: string;
        currency?: { __typename?: 'Currency'; code: string; places: number } | null;
      };
      contributor: { __typename?: 'CashFundContributor'; id: string; fullName: string; email: string };
    }>;
    publicTotalContributedAmount?: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    } | null;
    publicTotalGoal?: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    } | null;
    fixedContributionAmount?: {
      __typename?: 'MonetaryValue';
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      currency?: { __typename?: 'Currency'; code: string; places: number } | null;
    } | null;
    photo?: { __typename?: 'Photo'; id: string; url: string } | null;
  };
};

export type InitCashFundsHostMutationVariables = Types.Exact<{
  input: Types.CreateCashFundsHostInput;
  returnRoute?: Types.InputMaybe<Types.Scalars['String']>;
}>;

export type InitCashFundsHostMutation = {
  __typename?: 'Mutation';
  initCashFundsHost:
    | { __typename?: 'CashFundGeneralError'; code: string; nativeMessage?: string | null }
    | {
        __typename?: 'CashFundsHost';
        id: string;
        firstName?: string | null;
        lastName?: string | null;
        payoutAccount: {
          __typename?: 'CashFundsPayoutAccount';
          status: string;
          currencyCode: string;
          countryCode?: string | null;
          onboardingUrl?: string | null;
          balance: {
            __typename?: 'CashFundsPayoutAccountBalance';
            received: {
              __typename?: 'MonetaryValue';
              valueInMinorUnits: number;
              floatingPointDecimalString: string;
              currency?: { __typename?: 'Currency'; code: string; places: number } | null;
            };
            availablePending: {
              __typename?: 'MonetaryValue';
              valueInMinorUnits: number;
              floatingPointDecimalString: string;
              currency?: { __typename?: 'Currency'; code: string; places: number } | null;
            };
            available: {
              __typename?: 'MonetaryValue';
              valueInMinorUnits: number;
              floatingPointDecimalString: string;
              currency?: { __typename?: 'Currency'; code: string; places: number } | null;
            };
            withdrawnPending: {
              __typename?: 'MonetaryValue';
              valueInMinorUnits: number;
              floatingPointDecimalString: string;
              currency?: { __typename?: 'Currency'; code: string; places: number } | null;
            };
            withdrawn: {
              __typename?: 'MonetaryValue';
              valueInMinorUnits: number;
              floatingPointDecimalString: string;
              currency?: { __typename?: 'Currency'; code: string; places: number } | null;
            };
          };
          withdrawals: Array<{ __typename?: 'CashFundsPayoutAccountWithdrawal'; id: string; status: string }>;
        };
        cashFunds: Array<{
          __typename?: 'CashFund';
          hideGoal: boolean;
          id: string;
          name: string;
          note?: string | null;
          currencyCode: string;
          archived: boolean;
          totalGoal?: {
            __typename?: 'MonetaryValue';
            valueInMinorUnits: number;
            floatingPointDecimalString: string;
            currency?: { __typename?: 'Currency'; code: string; places: number } | null;
          } | null;
          totalContributedAmount?: {
            __typename?: 'MonetaryValue';
            valueInMinorUnits: number;
            floatingPointDecimalString: string;
            currency?: { __typename?: 'Currency'; code: string; places: number } | null;
          } | null;
          contributions: Array<{
            __typename?: 'CashFundContribution';
            id: string;
            message?: string | null;
            status: string;
            createdAt: string;
            amount: {
              __typename?: 'MonetaryValue';
              valueInMinorUnits: number;
              floatingPointDecimalString: string;
              currency?: { __typename?: 'Currency'; code: string; places: number } | null;
            };
            contributor: { __typename?: 'CashFundContributor'; id: string; fullName: string; email: string };
          }>;
          publicTotalContributedAmount?: {
            __typename?: 'MonetaryValue';
            valueInMinorUnits: number;
            floatingPointDecimalString: string;
            currency?: { __typename?: 'Currency'; code: string; places: number } | null;
          } | null;
          publicTotalGoal?: {
            __typename?: 'MonetaryValue';
            valueInMinorUnits: number;
            floatingPointDecimalString: string;
            currency?: { __typename?: 'Currency'; code: string; places: number } | null;
          } | null;
          fixedContributionAmount?: {
            __typename?: 'MonetaryValue';
            valueInMinorUnits: number;
            floatingPointDecimalString: string;
            currency?: { __typename?: 'Currency'; code: string; places: number } | null;
          } | null;
          photo?: { __typename?: 'Photo'; id: string; url: string } | null;
        }>;
      };
};

export type CreateCashFundMutationVariables = Types.Exact<{
  input: Types.CreateCashFundInput;
}>;

export type CreateCashFundMutation = {
  __typename?: 'Mutation';
  createCashFund:
    | {
        __typename?: 'CashFund';
        hideGoal: boolean;
        id: string;
        name: string;
        note?: string | null;
        currencyCode: string;
        archived: boolean;
        totalGoal?: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        } | null;
        totalContributedAmount?: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        } | null;
        contributions: Array<{
          __typename?: 'CashFundContribution';
          id: string;
          message?: string | null;
          status: string;
          createdAt: string;
          amount: {
            __typename?: 'MonetaryValue';
            valueInMinorUnits: number;
            floatingPointDecimalString: string;
            currency?: { __typename?: 'Currency'; code: string; places: number } | null;
          };
          contributor: { __typename?: 'CashFundContributor'; id: string; fullName: string; email: string };
        }>;
        publicTotalContributedAmount?: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        } | null;
        publicTotalGoal?: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        } | null;
        fixedContributionAmount?: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        } | null;
        photo?: { __typename?: 'Photo'; id: string; url: string } | null;
      }
    | { __typename?: 'CashFundGeneralError'; code: string; nativeMessage?: string | null };
};

export type UpdateCashFundMutationVariables = Types.Exact<{
  input: Types.UpdateCashFundInput;
}>;

export type UpdateCashFundMutation = {
  __typename?: 'Mutation';
  updateCashFund:
    | {
        __typename?: 'CashFund';
        hideGoal: boolean;
        id: string;
        name: string;
        note?: string | null;
        currencyCode: string;
        archived: boolean;
        totalGoal?: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        } | null;
        totalContributedAmount?: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        } | null;
        contributions: Array<{
          __typename?: 'CashFundContribution';
          id: string;
          message?: string | null;
          status: string;
          createdAt: string;
          amount: {
            __typename?: 'MonetaryValue';
            valueInMinorUnits: number;
            floatingPointDecimalString: string;
            currency?: { __typename?: 'Currency'; code: string; places: number } | null;
          };
          contributor: { __typename?: 'CashFundContributor'; id: string; fullName: string; email: string };
        }>;
        publicTotalContributedAmount?: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        } | null;
        publicTotalGoal?: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        } | null;
        fixedContributionAmount?: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        } | null;
        photo?: { __typename?: 'Photo'; id: string; url: string } | null;
      }
    | { __typename?: 'CashFundGeneralError'; code: string; nativeMessage?: string | null };
};

export type DuplicateCashFundMutationVariables = Types.Exact<{
  input: Types.DuplicateCashFundInput;
}>;

export type DuplicateCashFundMutation = {
  __typename?: 'Mutation';
  duplicateCashFund:
    | {
        __typename?: 'CashFund';
        hideGoal: boolean;
        id: string;
        name: string;
        note?: string | null;
        currencyCode: string;
        archived: boolean;
        totalGoal?: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        } | null;
        totalContributedAmount?: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        } | null;
        contributions: Array<{
          __typename?: 'CashFundContribution';
          id: string;
          message?: string | null;
          status: string;
          createdAt: string;
          amount: {
            __typename?: 'MonetaryValue';
            valueInMinorUnits: number;
            floatingPointDecimalString: string;
            currency?: { __typename?: 'Currency'; code: string; places: number } | null;
          };
          contributor: { __typename?: 'CashFundContributor'; id: string; fullName: string; email: string };
        }>;
        publicTotalContributedAmount?: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        } | null;
        publicTotalGoal?: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        } | null;
        fixedContributionAmount?: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        } | null;
        photo?: { __typename?: 'Photo'; id: string; url: string } | null;
      }
    | { __typename?: 'CashFundGeneralError'; code: string; nativeMessage?: string | null };
};

export type ArchiveCashFundMutationVariables = Types.Exact<{
  cashFundId: Types.Scalars['ID'];
}>;

export type ArchiveCashFundMutation = {
  __typename?: 'Mutation';
  archiveCashFund: { __typename?: 'ArchiveCashFundSuccess'; id: string } | { __typename?: 'CashFundGeneralError'; code: string; nativeMessage?: string | null };
};

export type ReactivateCashFundMutationVariables = Types.Exact<{
  cashFundId: Types.Scalars['ID'];
}>;

export type ReactivateCashFundMutation = {
  __typename?: 'Mutation';
  reactivateCashFund: { __typename?: 'CashFund'; id: string; archived: boolean } | { __typename?: 'CashFundGeneralError'; code: string; nativeMessage?: string | null };
};

export type CreateCashFundSolicitationMutationVariables = Types.Exact<{
  input: Types.CreateCashFundSolicitationInput;
}>;

export type CreateCashFundSolicitationMutation = {
  __typename?: 'Mutation';
  createCashFundSolicitation:
    | { __typename?: 'CashFundGeneralError'; code: string; nativeMessage?: string | null }
    | {
        __typename?: 'CashFundSolicitation';
        id: string;
        name?: string | null;
        status: string;
        inviteSubject: string;
        inviteMessage: string;
        config: {
          __typename?: 'CashFundSolicitationConfig';
          buttonText: string;
          salutation: string;
          includeRecipientNames: boolean;
          photo?: { __typename?: 'Photo'; id: string; url: string } | null;
        };
        invites: Array<{ __typename?: 'CashFundSolicitationInvite'; id: string; email: string; fullName?: string | null; guestKey?: string | null }>;
      };
};

export type UpdateCashFundSolicitationMutationVariables = Types.Exact<{
  input: Types.UpdateCashFundSolicitationInput;
}>;

export type UpdateCashFundSolicitationMutation = {
  __typename?: 'Mutation';
  updateCashFundSolicitation:
    | { __typename?: 'CashFundGeneralError'; code: string; nativeMessage?: string | null }
    | {
        __typename?: 'CashFundSolicitation';
        id: string;
        name?: string | null;
        status: string;
        inviteSubject: string;
        inviteMessage: string;
        config: {
          __typename?: 'CashFundSolicitationConfig';
          buttonText: string;
          salutation: string;
          includeRecipientNames: boolean;
          photo?: { __typename?: 'Photo'; id: string; url: string } | null;
        };
        invites: Array<{ __typename?: 'CashFundSolicitationInvite'; id: string; email: string; fullName?: string | null; guestKey?: string | null }>;
      };
};

export type ManageCashFundSolicitationRecipientsMutationVariables = Types.Exact<{
  input: Types.ManageCashFundSolicitationRecipientsInput;
}>;

export type ManageCashFundSolicitationRecipientsMutation = {
  __typename?: 'Mutation';
  manageCashFundSolicitationRecipients:
    | { __typename?: 'CashFundGeneralError'; code: string; nativeMessage?: string | null }
    | {
        __typename?: 'CashFundSolicitation';
        id: string;
        name?: string | null;
        status: string;
        inviteSubject: string;
        inviteMessage: string;
        config: {
          __typename?: 'CashFundSolicitationConfig';
          buttonText: string;
          salutation: string;
          includeRecipientNames: boolean;
          photo?: { __typename?: 'Photo'; id: string; url: string } | null;
        };
        invites: Array<{ __typename?: 'CashFundSolicitationInvite'; id: string; email: string; fullName?: string | null; guestKey?: string | null }>;
      };
};

export type SendCashFundSolicitationEmailsMutationVariables = Types.Exact<{
  solicitationId: Types.Scalars['ID'];
}>;

export type SendCashFundSolicitationEmailsMutation = {
  __typename?: 'Mutation';
  sendCashFundSolicitationEmails:
    | { __typename?: 'CashFundGeneralError'; code: string; nativeMessage?: string | null }
    | { __typename?: 'SendCashFundSolicitationEmailsSuccess'; id: string };
};

export type SendCashFundSolicitationTestEmailMutationVariables = Types.Exact<{
  input: Types.SendCashFundSolicitationTestEmailInput;
}>;

export type SendCashFundSolicitationTestEmailMutation = {
  __typename?: 'Mutation';
  sendCashFundSolicitationTestEmail:
    | { __typename?: 'CashFundGeneralError'; code: string; nativeMessage?: string | null }
    | { __typename?: 'SendCashFundSolicitationTestEmailSuccess'; id: string };
};

export type AcceptCashFundContributionMutationVariables = Types.Exact<{
  contributionId: Types.Scalars['ID'];
}>;

export type AcceptCashFundContributionMutation = {
  __typename?: 'Mutation';
  acceptCashFundContribution:
    | {
        __typename?: 'CashFundContribution';
        id: string;
        message?: string | null;
        status: string;
        createdAt: string;
        order: {
          __typename?: 'CashFundOrder';
          id: string;
          status: string;
          contributionAmount: {
            __typename?: 'MonetaryValue';
            valueInMinorUnits: number;
            floatingPointDecimalString: string;
            currency?: { __typename?: 'Currency'; code: string; places: number } | null;
          };
          totalTaxes: {
            __typename?: 'MonetaryValue';
            valueInMinorUnits: number;
            floatingPointDecimalString: string;
            currency?: { __typename?: 'Currency'; code: string; places: number } | null;
          };
          totalFees: {
            __typename?: 'MonetaryValue';
            valueInMinorUnits: number;
            floatingPointDecimalString: string;
            currency?: { __typename?: 'Currency'; code: string; places: number } | null;
          };
          total: {
            __typename?: 'MonetaryValue';
            valueInMinorUnits: number;
            floatingPointDecimalString: string;
            currency?: { __typename?: 'Currency'; code: string; places: number } | null;
          };
        };
        amount: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        };
        contributor: { __typename?: 'CashFundContributor'; id: string; fullName: string; email: string };
      }
    | { __typename?: 'CashFundGeneralError'; code: string; nativeMessage?: string | null };
};

export type RefundCashFundContributionMutationVariables = Types.Exact<{
  contributionId: Types.Scalars['ID'];
}>;

export type RefundCashFundContributionMutation = {
  __typename?: 'Mutation';
  refundCashFundContribution:
    | {
        __typename?: 'CashFundContribution';
        id: string;
        message?: string | null;
        status: string;
        createdAt: string;
        amount: {
          __typename?: 'MonetaryValue';
          valueInMinorUnits: number;
          floatingPointDecimalString: string;
          currency?: { __typename?: 'Currency'; code: string; places: number } | null;
        };
        contributor: { __typename?: 'CashFundContributor'; id: string; fullName: string; email: string };
      }
    | { __typename?: 'CashFundGeneralError'; code: string; nativeMessage?: string | null };
};

export type WithdrawCashFundsPayoutAccountMutationVariables = Types.Exact<{
  eventId: Types.Scalars['ID'];
}>;

export type WithdrawCashFundsPayoutAccountMutation = {
  __typename?: 'Mutation';
  withdrawCashFundsPayoutAccount:
    | { __typename?: 'CashFundGeneralError'; code: string; nativeMessage?: string | null }
    | { __typename?: 'CashFundsPayoutAccountWithdrawal'; id: string; status: string };
};

export const CashFundGeneralErrorFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundGeneralError' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nativeMessage' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const CashFundOrderFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundOrder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributionAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalTaxes' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalFees' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'total' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const PublicCashFundsHostFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PublicCashFundsHost' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundsHost' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const CashFundsPayoutAccountFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundsPayoutAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundsPayoutAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'currencyCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'onboardingUrl' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'params' },
                value: {
                  kind: 'ObjectValue',
                  fields: [{ kind: 'ObjectField', name: { kind: 'Name', value: 'returnRoute' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'returnRoute' } } }],
                },
              },
            ],
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'balance' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'received' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availablePending' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'available' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'withdrawnPending' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'withdrawn' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'withdrawals' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const CashFundPhotoFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundPhoto' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const PublicCashFundFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PublicCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'currencyCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fixedContributionAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'archived' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'photo' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundPhoto' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundPhoto' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const CashFundContributorFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContributor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContributor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const CashFundContributionFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContribution' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContribution' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'amount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributor' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContributor' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContributor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContributor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const AdminCashFundFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PublicCashFund' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hideGoal' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributions' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContribution' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundPhoto' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PublicCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'currencyCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fixedContributionAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'archived' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'photo' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundPhoto' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContributor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContributor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContribution' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContribution' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'amount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributor' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContributor' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const AdminCashFundsHostFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminCashFundsHost' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundsHost' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PublicCashFundsHost' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payoutAccount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundsPayoutAccount' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cashFunds' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AdminCashFund' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundPhoto' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PublicCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'currencyCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fixedContributionAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'archived' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'photo' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundPhoto' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PublicCashFund' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hideGoal' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributions' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContribution' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContributor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContributor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContribution' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContribution' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'amount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributor' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContributor' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundsPayoutAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundsPayoutAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'currencyCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'onboardingUrl' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'params' },
                value: {
                  kind: 'ObjectValue',
                  fields: [{ kind: 'ObjectField', name: { kind: 'Name', value: 'returnRoute' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'returnRoute' } } }],
                },
              },
            ],
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'balance' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'received' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availablePending' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'available' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'withdrawnPending' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'withdrawn' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'withdrawals' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PublicCashFundsHost' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundsHost' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const CashFundInviteFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundInvite' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundSolicitationInvite' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'guestKey' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const CashFundSolicitationFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundSolicitation' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundSolicitation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'inviteSubject' } },
          { kind: 'Field', name: { kind: 'Name', value: 'inviteMessage' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'config' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'photo' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundPhoto' } }] },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'buttonText' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salutation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'includeRecipientNames' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'invites' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundInvite' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundPhoto' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundInvite' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundSolicitationInvite' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'guestKey' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const FindCashFundsHostByEventIdDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'FindCashFundsHostByEventId' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'eventId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'returnRoute' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'findCashFundsHostByEventId' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'eventId' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'eventId' } } }],
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AdminCashFundsHost' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundPhoto' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PublicCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'currencyCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fixedContributionAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'archived' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'photo' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundPhoto' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PublicCashFund' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hideGoal' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributions' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContribution' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContributor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContributor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContribution' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContribution' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'amount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributor' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContributor' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundsPayoutAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundsPayoutAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'currencyCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'onboardingUrl' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'params' },
                value: {
                  kind: 'ObjectValue',
                  fields: [{ kind: 'ObjectField', name: { kind: 'Name', value: 'returnRoute' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'returnRoute' } } }],
                },
              },
            ],
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'balance' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'received' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availablePending' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'available' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'withdrawnPending' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'withdrawn' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'withdrawals' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PublicCashFundsHost' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundsHost' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminCashFundsHost' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundsHost' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PublicCashFundsHost' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payoutAccount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundsPayoutAccount' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cashFunds' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AdminCashFund' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useFindCashFundsHostByEventIdQuery__
 *
 * To run a query within a React component, call `useFindCashFundsHostByEventIdQuery` and pass it any options that fit your needs.
 * When your component renders, `useFindCashFundsHostByEventIdQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useFindCashFundsHostByEventIdQuery({
 *   variables: {
 *      eventId: // value for 'eventId'
 *      returnRoute: // value for 'returnRoute'
 *   },
 * });
 */
export function useFindCashFundsHostByEventIdQuery(baseOptions?: ApolloReactHooks.QueryHookOptions<FindCashFundsHostByEventIdQuery, FindCashFundsHostByEventIdQueryVariables>) {
  return ApolloReactHooks.useQuery<FindCashFundsHostByEventIdQuery, FindCashFundsHostByEventIdQueryVariables>(FindCashFundsHostByEventIdDocument, baseOptions);
}
export function useFindCashFundsHostByEventIdLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<FindCashFundsHostByEventIdQuery, FindCashFundsHostByEventIdQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<FindCashFundsHostByEventIdQuery, FindCashFundsHostByEventIdQueryVariables>(FindCashFundsHostByEventIdDocument, baseOptions);
}
export type FindCashFundsHostByEventIdQueryHookResult = ReturnType<typeof useFindCashFundsHostByEventIdQuery>;
export type FindCashFundsHostByEventIdLazyQueryHookResult = ReturnType<typeof useFindCashFundsHostByEventIdLazyQuery>;
export type FindCashFundsHostByEventIdQueryResult = Apollo.QueryResult<FindCashFundsHostByEventIdQuery, FindCashFundsHostByEventIdQueryVariables>;
export const GetCashFundsByEventIdDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetCashFundsByEventId' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'eventId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'getCashFundsByEventId' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'eventId' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'eventId' } } }],
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AdminCashFund' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundPhoto' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PublicCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'currencyCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fixedContributionAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'archived' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'photo' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundPhoto' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PublicCashFund' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hideGoal' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributions' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContribution' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContributor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContributor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContribution' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContribution' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'amount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributor' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContributor' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useGetCashFundsByEventIdQuery__
 *
 * To run a query within a React component, call `useGetCashFundsByEventIdQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetCashFundsByEventIdQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetCashFundsByEventIdQuery({
 *   variables: {
 *      eventId: // value for 'eventId'
 *   },
 * });
 */
export function useGetCashFundsByEventIdQuery(baseOptions?: ApolloReactHooks.QueryHookOptions<GetCashFundsByEventIdQuery, GetCashFundsByEventIdQueryVariables>) {
  return ApolloReactHooks.useQuery<GetCashFundsByEventIdQuery, GetCashFundsByEventIdQueryVariables>(GetCashFundsByEventIdDocument, baseOptions);
}
export function useGetCashFundsByEventIdLazyQuery(baseOptions?: ApolloReactHooks.LazyQueryHookOptions<GetCashFundsByEventIdQuery, GetCashFundsByEventIdQueryVariables>) {
  return ApolloReactHooks.useLazyQuery<GetCashFundsByEventIdQuery, GetCashFundsByEventIdQueryVariables>(GetCashFundsByEventIdDocument, baseOptions);
}
export type GetCashFundsByEventIdQueryHookResult = ReturnType<typeof useGetCashFundsByEventIdQuery>;
export type GetCashFundsByEventIdLazyQueryHookResult = ReturnType<typeof useGetCashFundsByEventIdLazyQuery>;
export type GetCashFundsByEventIdQueryResult = Apollo.QueryResult<GetCashFundsByEventIdQuery, GetCashFundsByEventIdQueryVariables>;
export const GetCashFundByIdDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetCashFundById' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'getCashFundById' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'id' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } } }],
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AdminCashFund' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundPhoto' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PublicCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'currencyCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fixedContributionAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'archived' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'photo' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundPhoto' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PublicCashFund' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hideGoal' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributions' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContribution' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContributor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContributor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContribution' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContribution' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'amount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributor' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContributor' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useGetCashFundByIdQuery__
 *
 * To run a query within a React component, call `useGetCashFundByIdQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetCashFundByIdQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetCashFundByIdQuery({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useGetCashFundByIdQuery(baseOptions?: ApolloReactHooks.QueryHookOptions<GetCashFundByIdQuery, GetCashFundByIdQueryVariables>) {
  return ApolloReactHooks.useQuery<GetCashFundByIdQuery, GetCashFundByIdQueryVariables>(GetCashFundByIdDocument, baseOptions);
}
export function useGetCashFundByIdLazyQuery(baseOptions?: ApolloReactHooks.LazyQueryHookOptions<GetCashFundByIdQuery, GetCashFundByIdQueryVariables>) {
  return ApolloReactHooks.useLazyQuery<GetCashFundByIdQuery, GetCashFundByIdQueryVariables>(GetCashFundByIdDocument, baseOptions);
}
export type GetCashFundByIdQueryHookResult = ReturnType<typeof useGetCashFundByIdQuery>;
export type GetCashFundByIdLazyQueryHookResult = ReturnType<typeof useGetCashFundByIdLazyQuery>;
export type GetCashFundByIdQueryResult = Apollo.QueryResult<GetCashFundByIdQuery, GetCashFundByIdQueryVariables>;
export const InitCashFundsHostDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'InitCashFundsHost' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'CreateCashFundsHostInput' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'returnRoute' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'initCashFundsHost' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'input' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundsHost' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AdminCashFundsHost' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundGeneralError' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundGeneralError' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nativeMessage' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundPhoto' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PublicCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'currencyCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fixedContributionAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'archived' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'photo' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundPhoto' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PublicCashFund' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hideGoal' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributions' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContribution' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContributor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContributor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContribution' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContribution' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'amount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributor' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContributor' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundsPayoutAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundsPayoutAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'currencyCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'onboardingUrl' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'params' },
                value: {
                  kind: 'ObjectValue',
                  fields: [{ kind: 'ObjectField', name: { kind: 'Name', value: 'returnRoute' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'returnRoute' } } }],
                },
              },
            ],
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'balance' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'received' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availablePending' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'available' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'withdrawnPending' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'withdrawn' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'withdrawals' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PublicCashFundsHost' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundsHost' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminCashFundsHost' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundsHost' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PublicCashFundsHost' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payoutAccount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundsPayoutAccount' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cashFunds' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AdminCashFund' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type InitCashFundsHostMutationFn = Apollo.MutationFunction<InitCashFundsHostMutation, InitCashFundsHostMutationVariables>;

/**
 * __useInitCashFundsHostMutation__
 *
 * To run a mutation, you first call `useInitCashFundsHostMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useInitCashFundsHostMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [initCashFundsHostMutation, { data, loading, error }] = useInitCashFundsHostMutation({
 *   variables: {
 *      input: // value for 'input'
 *      returnRoute: // value for 'returnRoute'
 *   },
 * });
 */
export function useInitCashFundsHostMutation(baseOptions?: ApolloReactHooks.MutationHookOptions<InitCashFundsHostMutation, InitCashFundsHostMutationVariables>) {
  return ApolloReactHooks.useMutation<InitCashFundsHostMutation, InitCashFundsHostMutationVariables>(InitCashFundsHostDocument, baseOptions);
}
export type InitCashFundsHostMutationHookResult = ReturnType<typeof useInitCashFundsHostMutation>;
export type InitCashFundsHostMutationResult = Apollo.MutationResult<InitCashFundsHostMutation>;
export type InitCashFundsHostMutationOptions = Apollo.BaseMutationOptions<InitCashFundsHostMutation, InitCashFundsHostMutationVariables>;
export const CreateCashFundDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CreateCashFund' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'CreateCashFundInput' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createCashFund' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'input' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AdminCashFund' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundGeneralError' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundGeneralError' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nativeMessage' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundPhoto' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PublicCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'currencyCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fixedContributionAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'archived' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'photo' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundPhoto' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PublicCashFund' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hideGoal' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributions' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContribution' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContributor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContributor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContribution' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContribution' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'amount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributor' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContributor' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type CreateCashFundMutationFn = Apollo.MutationFunction<CreateCashFundMutation, CreateCashFundMutationVariables>;

/**
 * __useCreateCashFundMutation__
 *
 * To run a mutation, you first call `useCreateCashFundMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateCashFundMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createCashFundMutation, { data, loading, error }] = useCreateCashFundMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
export function useCreateCashFundMutation(baseOptions?: ApolloReactHooks.MutationHookOptions<CreateCashFundMutation, CreateCashFundMutationVariables>) {
  return ApolloReactHooks.useMutation<CreateCashFundMutation, CreateCashFundMutationVariables>(CreateCashFundDocument, baseOptions);
}
export type CreateCashFundMutationHookResult = ReturnType<typeof useCreateCashFundMutation>;
export type CreateCashFundMutationResult = Apollo.MutationResult<CreateCashFundMutation>;
export type CreateCashFundMutationOptions = Apollo.BaseMutationOptions<CreateCashFundMutation, CreateCashFundMutationVariables>;
export const UpdateCashFundDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'UpdateCashFund' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'UpdateCashFundInput' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateCashFund' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'input' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AdminCashFund' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundGeneralError' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundGeneralError' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nativeMessage' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundPhoto' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PublicCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'currencyCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fixedContributionAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'archived' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'photo' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundPhoto' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PublicCashFund' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hideGoal' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributions' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContribution' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContributor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContributor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContribution' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContribution' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'amount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributor' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContributor' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type UpdateCashFundMutationFn = Apollo.MutationFunction<UpdateCashFundMutation, UpdateCashFundMutationVariables>;

/**
 * __useUpdateCashFundMutation__
 *
 * To run a mutation, you first call `useUpdateCashFundMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateCashFundMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateCashFundMutation, { data, loading, error }] = useUpdateCashFundMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
export function useUpdateCashFundMutation(baseOptions?: ApolloReactHooks.MutationHookOptions<UpdateCashFundMutation, UpdateCashFundMutationVariables>) {
  return ApolloReactHooks.useMutation<UpdateCashFundMutation, UpdateCashFundMutationVariables>(UpdateCashFundDocument, baseOptions);
}
export type UpdateCashFundMutationHookResult = ReturnType<typeof useUpdateCashFundMutation>;
export type UpdateCashFundMutationResult = Apollo.MutationResult<UpdateCashFundMutation>;
export type UpdateCashFundMutationOptions = Apollo.BaseMutationOptions<UpdateCashFundMutation, UpdateCashFundMutationVariables>;
export const DuplicateCashFundDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'DuplicateCashFund' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'DuplicateCashFundInput' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'duplicateCashFund' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'input' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AdminCashFund' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundGeneralError' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundGeneralError' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nativeMessage' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundPhoto' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PublicCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'currencyCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'publicTotalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fixedContributionAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'archived' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'photo' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundPhoto' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminCashFund' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PublicCashFund' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hideGoal' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalGoal' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalContributedAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributions' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContribution' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContributor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContributor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContribution' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContribution' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'amount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributor' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContributor' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type DuplicateCashFundMutationFn = Apollo.MutationFunction<DuplicateCashFundMutation, DuplicateCashFundMutationVariables>;

/**
 * __useDuplicateCashFundMutation__
 *
 * To run a mutation, you first call `useDuplicateCashFundMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDuplicateCashFundMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [duplicateCashFundMutation, { data, loading, error }] = useDuplicateCashFundMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
export function useDuplicateCashFundMutation(baseOptions?: ApolloReactHooks.MutationHookOptions<DuplicateCashFundMutation, DuplicateCashFundMutationVariables>) {
  return ApolloReactHooks.useMutation<DuplicateCashFundMutation, DuplicateCashFundMutationVariables>(DuplicateCashFundDocument, baseOptions);
}
export type DuplicateCashFundMutationHookResult = ReturnType<typeof useDuplicateCashFundMutation>;
export type DuplicateCashFundMutationResult = Apollo.MutationResult<DuplicateCashFundMutation>;
export type DuplicateCashFundMutationOptions = Apollo.BaseMutationOptions<DuplicateCashFundMutation, DuplicateCashFundMutationVariables>;
export const ArchiveCashFundDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ArchiveCashFund' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'cashFundId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'archiveCashFund' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'cashFundId' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'cashFundId' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ArchiveCashFundSuccess' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundGeneralError' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundGeneralError' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nativeMessage' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type ArchiveCashFundMutationFn = Apollo.MutationFunction<ArchiveCashFundMutation, ArchiveCashFundMutationVariables>;

/**
 * __useArchiveCashFundMutation__
 *
 * To run a mutation, you first call `useArchiveCashFundMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useArchiveCashFundMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [archiveCashFundMutation, { data, loading, error }] = useArchiveCashFundMutation({
 *   variables: {
 *      cashFundId: // value for 'cashFundId'
 *   },
 * });
 */
export function useArchiveCashFundMutation(baseOptions?: ApolloReactHooks.MutationHookOptions<ArchiveCashFundMutation, ArchiveCashFundMutationVariables>) {
  return ApolloReactHooks.useMutation<ArchiveCashFundMutation, ArchiveCashFundMutationVariables>(ArchiveCashFundDocument, baseOptions);
}
export type ArchiveCashFundMutationHookResult = ReturnType<typeof useArchiveCashFundMutation>;
export type ArchiveCashFundMutationResult = Apollo.MutationResult<ArchiveCashFundMutation>;
export type ArchiveCashFundMutationOptions = Apollo.BaseMutationOptions<ArchiveCashFundMutation, ArchiveCashFundMutationVariables>;
export const ReactivateCashFundDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ReactivateCashFund' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'cashFundId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reactivateCashFund' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'cashFundId' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'cashFundId' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFund' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'archived' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundGeneralError' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundGeneralError' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nativeMessage' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type ReactivateCashFundMutationFn = Apollo.MutationFunction<ReactivateCashFundMutation, ReactivateCashFundMutationVariables>;

/**
 * __useReactivateCashFundMutation__
 *
 * To run a mutation, you first call `useReactivateCashFundMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useReactivateCashFundMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [reactivateCashFundMutation, { data, loading, error }] = useReactivateCashFundMutation({
 *   variables: {
 *      cashFundId: // value for 'cashFundId'
 *   },
 * });
 */
export function useReactivateCashFundMutation(baseOptions?: ApolloReactHooks.MutationHookOptions<ReactivateCashFundMutation, ReactivateCashFundMutationVariables>) {
  return ApolloReactHooks.useMutation<ReactivateCashFundMutation, ReactivateCashFundMutationVariables>(ReactivateCashFundDocument, baseOptions);
}
export type ReactivateCashFundMutationHookResult = ReturnType<typeof useReactivateCashFundMutation>;
export type ReactivateCashFundMutationResult = Apollo.MutationResult<ReactivateCashFundMutation>;
export type ReactivateCashFundMutationOptions = Apollo.BaseMutationOptions<ReactivateCashFundMutation, ReactivateCashFundMutationVariables>;
export const CreateCashFundSolicitationDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CreateCashFundSolicitation' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'CreateCashFundSolicitationInput' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createCashFundSolicitation' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'input' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundSolicitation' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundSolicitation' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundGeneralError' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundGeneralError' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nativeMessage' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundPhoto' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundInvite' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundSolicitationInvite' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'guestKey' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundSolicitation' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundSolicitation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'inviteSubject' } },
          { kind: 'Field', name: { kind: 'Name', value: 'inviteMessage' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'config' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'photo' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundPhoto' } }] },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'buttonText' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salutation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'includeRecipientNames' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'invites' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundInvite' } }] },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type CreateCashFundSolicitationMutationFn = Apollo.MutationFunction<CreateCashFundSolicitationMutation, CreateCashFundSolicitationMutationVariables>;

/**
 * __useCreateCashFundSolicitationMutation__
 *
 * To run a mutation, you first call `useCreateCashFundSolicitationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateCashFundSolicitationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createCashFundSolicitationMutation, { data, loading, error }] = useCreateCashFundSolicitationMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
export function useCreateCashFundSolicitationMutation(
  baseOptions?: ApolloReactHooks.MutationHookOptions<CreateCashFundSolicitationMutation, CreateCashFundSolicitationMutationVariables>,
) {
  return ApolloReactHooks.useMutation<CreateCashFundSolicitationMutation, CreateCashFundSolicitationMutationVariables>(CreateCashFundSolicitationDocument, baseOptions);
}
export type CreateCashFundSolicitationMutationHookResult = ReturnType<typeof useCreateCashFundSolicitationMutation>;
export type CreateCashFundSolicitationMutationResult = Apollo.MutationResult<CreateCashFundSolicitationMutation>;
export type CreateCashFundSolicitationMutationOptions = Apollo.BaseMutationOptions<CreateCashFundSolicitationMutation, CreateCashFundSolicitationMutationVariables>;
export const UpdateCashFundSolicitationDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'UpdateCashFundSolicitation' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'UpdateCashFundSolicitationInput' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateCashFundSolicitation' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'input' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundSolicitation' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundSolicitation' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundGeneralError' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundGeneralError' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nativeMessage' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundPhoto' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundInvite' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundSolicitationInvite' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'guestKey' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundSolicitation' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundSolicitation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'inviteSubject' } },
          { kind: 'Field', name: { kind: 'Name', value: 'inviteMessage' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'config' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'photo' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundPhoto' } }] },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'buttonText' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salutation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'includeRecipientNames' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'invites' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundInvite' } }] },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type UpdateCashFundSolicitationMutationFn = Apollo.MutationFunction<UpdateCashFundSolicitationMutation, UpdateCashFundSolicitationMutationVariables>;

/**
 * __useUpdateCashFundSolicitationMutation__
 *
 * To run a mutation, you first call `useUpdateCashFundSolicitationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateCashFundSolicitationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateCashFundSolicitationMutation, { data, loading, error }] = useUpdateCashFundSolicitationMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
export function useUpdateCashFundSolicitationMutation(
  baseOptions?: ApolloReactHooks.MutationHookOptions<UpdateCashFundSolicitationMutation, UpdateCashFundSolicitationMutationVariables>,
) {
  return ApolloReactHooks.useMutation<UpdateCashFundSolicitationMutation, UpdateCashFundSolicitationMutationVariables>(UpdateCashFundSolicitationDocument, baseOptions);
}
export type UpdateCashFundSolicitationMutationHookResult = ReturnType<typeof useUpdateCashFundSolicitationMutation>;
export type UpdateCashFundSolicitationMutationResult = Apollo.MutationResult<UpdateCashFundSolicitationMutation>;
export type UpdateCashFundSolicitationMutationOptions = Apollo.BaseMutationOptions<UpdateCashFundSolicitationMutation, UpdateCashFundSolicitationMutationVariables>;
export const ManageCashFundSolicitationRecipientsDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ManageCashFundSolicitationRecipients' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ManageCashFundSolicitationRecipientsInput' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'manageCashFundSolicitationRecipients' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'input' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundSolicitation' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundSolicitation' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundGeneralError' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundGeneralError' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nativeMessage' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundPhoto' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundInvite' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundSolicitationInvite' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'guestKey' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundSolicitation' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundSolicitation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'inviteSubject' } },
          { kind: 'Field', name: { kind: 'Name', value: 'inviteMessage' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'config' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'photo' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundPhoto' } }] },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'buttonText' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salutation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'includeRecipientNames' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'invites' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundInvite' } }] },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type ManageCashFundSolicitationRecipientsMutationFn = Apollo.MutationFunction<
  ManageCashFundSolicitationRecipientsMutation,
  ManageCashFundSolicitationRecipientsMutationVariables
>;

/**
 * __useManageCashFundSolicitationRecipientsMutation__
 *
 * To run a mutation, you first call `useManageCashFundSolicitationRecipientsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useManageCashFundSolicitationRecipientsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [manageCashFundSolicitationRecipientsMutation, { data, loading, error }] = useManageCashFundSolicitationRecipientsMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
export function useManageCashFundSolicitationRecipientsMutation(
  baseOptions?: ApolloReactHooks.MutationHookOptions<ManageCashFundSolicitationRecipientsMutation, ManageCashFundSolicitationRecipientsMutationVariables>,
) {
  return ApolloReactHooks.useMutation<ManageCashFundSolicitationRecipientsMutation, ManageCashFundSolicitationRecipientsMutationVariables>(
    ManageCashFundSolicitationRecipientsDocument,
    baseOptions,
  );
}
export type ManageCashFundSolicitationRecipientsMutationHookResult = ReturnType<typeof useManageCashFundSolicitationRecipientsMutation>;
export type ManageCashFundSolicitationRecipientsMutationResult = Apollo.MutationResult<ManageCashFundSolicitationRecipientsMutation>;
export type ManageCashFundSolicitationRecipientsMutationOptions = Apollo.BaseMutationOptions<
  ManageCashFundSolicitationRecipientsMutation,
  ManageCashFundSolicitationRecipientsMutationVariables
>;
export const SendCashFundSolicitationEmailsDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'SendCashFundSolicitationEmails' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'solicitationId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sendCashFundSolicitationEmails' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'solicitationId' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'solicitationId' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SendCashFundSolicitationEmailsSuccess' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundGeneralError' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundGeneralError' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nativeMessage' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type SendCashFundSolicitationEmailsMutationFn = Apollo.MutationFunction<SendCashFundSolicitationEmailsMutation, SendCashFundSolicitationEmailsMutationVariables>;

/**
 * __useSendCashFundSolicitationEmailsMutation__
 *
 * To run a mutation, you first call `useSendCashFundSolicitationEmailsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useSendCashFundSolicitationEmailsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [sendCashFundSolicitationEmailsMutation, { data, loading, error }] = useSendCashFundSolicitationEmailsMutation({
 *   variables: {
 *      solicitationId: // value for 'solicitationId'
 *   },
 * });
 */
export function useSendCashFundSolicitationEmailsMutation(
  baseOptions?: ApolloReactHooks.MutationHookOptions<SendCashFundSolicitationEmailsMutation, SendCashFundSolicitationEmailsMutationVariables>,
) {
  return ApolloReactHooks.useMutation<SendCashFundSolicitationEmailsMutation, SendCashFundSolicitationEmailsMutationVariables>(SendCashFundSolicitationEmailsDocument, baseOptions);
}
export type SendCashFundSolicitationEmailsMutationHookResult = ReturnType<typeof useSendCashFundSolicitationEmailsMutation>;
export type SendCashFundSolicitationEmailsMutationResult = Apollo.MutationResult<SendCashFundSolicitationEmailsMutation>;
export type SendCashFundSolicitationEmailsMutationOptions = Apollo.BaseMutationOptions<SendCashFundSolicitationEmailsMutation, SendCashFundSolicitationEmailsMutationVariables>;
export const SendCashFundSolicitationTestEmailDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'SendCashFundSolicitationTestEmail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'SendCashFundSolicitationTestEmailInput' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sendCashFundSolicitationTestEmail' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'input' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SendCashFundSolicitationTestEmailSuccess' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundGeneralError' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundGeneralError' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nativeMessage' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type SendCashFundSolicitationTestEmailMutationFn = Apollo.MutationFunction<SendCashFundSolicitationTestEmailMutation, SendCashFundSolicitationTestEmailMutationVariables>;

/**
 * __useSendCashFundSolicitationTestEmailMutation__
 *
 * To run a mutation, you first call `useSendCashFundSolicitationTestEmailMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useSendCashFundSolicitationTestEmailMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [sendCashFundSolicitationTestEmailMutation, { data, loading, error }] = useSendCashFundSolicitationTestEmailMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
export function useSendCashFundSolicitationTestEmailMutation(
  baseOptions?: ApolloReactHooks.MutationHookOptions<SendCashFundSolicitationTestEmailMutation, SendCashFundSolicitationTestEmailMutationVariables>,
) {
  return ApolloReactHooks.useMutation<SendCashFundSolicitationTestEmailMutation, SendCashFundSolicitationTestEmailMutationVariables>(
    SendCashFundSolicitationTestEmailDocument,
    baseOptions,
  );
}
export type SendCashFundSolicitationTestEmailMutationHookResult = ReturnType<typeof useSendCashFundSolicitationTestEmailMutation>;
export type SendCashFundSolicitationTestEmailMutationResult = Apollo.MutationResult<SendCashFundSolicitationTestEmailMutation>;
export type SendCashFundSolicitationTestEmailMutationOptions = Apollo.BaseMutationOptions<
  SendCashFundSolicitationTestEmailMutation,
  SendCashFundSolicitationTestEmailMutationVariables
>;
export const AcceptCashFundContributionDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AcceptCashFundContribution' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'contributionId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'acceptCashFundContribution' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'contributionId' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'contributionId' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContribution' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContribution' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'order' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundOrder' } }] },
                      },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundGeneralError' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundGeneralError' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nativeMessage' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContributor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContributor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContribution' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContribution' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'amount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributor' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContributor' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundOrder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributionAmount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalTaxes' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalFees' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'total' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type AcceptCashFundContributionMutationFn = Apollo.MutationFunction<AcceptCashFundContributionMutation, AcceptCashFundContributionMutationVariables>;

/**
 * __useAcceptCashFundContributionMutation__
 *
 * To run a mutation, you first call `useAcceptCashFundContributionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useAcceptCashFundContributionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [acceptCashFundContributionMutation, { data, loading, error }] = useAcceptCashFundContributionMutation({
 *   variables: {
 *      contributionId: // value for 'contributionId'
 *   },
 * });
 */
export function useAcceptCashFundContributionMutation(
  baseOptions?: ApolloReactHooks.MutationHookOptions<AcceptCashFundContributionMutation, AcceptCashFundContributionMutationVariables>,
) {
  return ApolloReactHooks.useMutation<AcceptCashFundContributionMutation, AcceptCashFundContributionMutationVariables>(AcceptCashFundContributionDocument, baseOptions);
}
export type AcceptCashFundContributionMutationHookResult = ReturnType<typeof useAcceptCashFundContributionMutation>;
export type AcceptCashFundContributionMutationResult = Apollo.MutationResult<AcceptCashFundContributionMutation>;
export type AcceptCashFundContributionMutationOptions = Apollo.BaseMutationOptions<AcceptCashFundContributionMutation, AcceptCashFundContributionMutationVariables>;
export const RefundCashFundContributionDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'RefundCashFundContribution' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'contributionId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'refundCashFundContribution' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'contributionId' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'contributionId' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContribution' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContribution' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundGeneralError' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundGeneralError' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nativeMessage' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContributor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContributor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundContribution' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundContribution' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'amount' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MonetaryValue' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contributor' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundContributor' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MonetaryValue' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MonetaryValue' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
          { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'places' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type RefundCashFundContributionMutationFn = Apollo.MutationFunction<RefundCashFundContributionMutation, RefundCashFundContributionMutationVariables>;

/**
 * __useRefundCashFundContributionMutation__
 *
 * To run a mutation, you first call `useRefundCashFundContributionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRefundCashFundContributionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [refundCashFundContributionMutation, { data, loading, error }] = useRefundCashFundContributionMutation({
 *   variables: {
 *      contributionId: // value for 'contributionId'
 *   },
 * });
 */
export function useRefundCashFundContributionMutation(
  baseOptions?: ApolloReactHooks.MutationHookOptions<RefundCashFundContributionMutation, RefundCashFundContributionMutationVariables>,
) {
  return ApolloReactHooks.useMutation<RefundCashFundContributionMutation, RefundCashFundContributionMutationVariables>(RefundCashFundContributionDocument, baseOptions);
}
export type RefundCashFundContributionMutationHookResult = ReturnType<typeof useRefundCashFundContributionMutation>;
export type RefundCashFundContributionMutationResult = Apollo.MutationResult<RefundCashFundContributionMutation>;
export type RefundCashFundContributionMutationOptions = Apollo.BaseMutationOptions<RefundCashFundContributionMutation, RefundCashFundContributionMutationVariables>;
export const WithdrawCashFundsPayoutAccountDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'WithdrawCashFundsPayoutAccount' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'eventId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'withdrawCashFundsPayoutAccount' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'eventId' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'eventId' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundsPayoutAccountWithdrawal' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CashFundGeneralError' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CashFundGeneralError' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CashFundGeneralError' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nativeMessage' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type WithdrawCashFundsPayoutAccountMutationFn = Apollo.MutationFunction<WithdrawCashFundsPayoutAccountMutation, WithdrawCashFundsPayoutAccountMutationVariables>;

/**
 * __useWithdrawCashFundsPayoutAccountMutation__
 *
 * To run a mutation, you first call `useWithdrawCashFundsPayoutAccountMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useWithdrawCashFundsPayoutAccountMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [withdrawCashFundsPayoutAccountMutation, { data, loading, error }] = useWithdrawCashFundsPayoutAccountMutation({
 *   variables: {
 *      eventId: // value for 'eventId'
 *   },
 * });
 */
export function useWithdrawCashFundsPayoutAccountMutation(
  baseOptions?: ApolloReactHooks.MutationHookOptions<WithdrawCashFundsPayoutAccountMutation, WithdrawCashFundsPayoutAccountMutationVariables>,
) {
  return ApolloReactHooks.useMutation<WithdrawCashFundsPayoutAccountMutation, WithdrawCashFundsPayoutAccountMutationVariables>(WithdrawCashFundsPayoutAccountDocument, baseOptions);
}
export type WithdrawCashFundsPayoutAccountMutationHookResult = ReturnType<typeof useWithdrawCashFundsPayoutAccountMutation>;
export type WithdrawCashFundsPayoutAccountMutationResult = Apollo.MutationResult<WithdrawCashFundsPayoutAccountMutation>;
export type WithdrawCashFundsPayoutAccountMutationOptions = Apollo.BaseMutationOptions<WithdrawCashFundsPayoutAccountMutation, WithdrawCashFundsPayoutAccountMutationVariables>;
